/** Agent coordination types for the 4-specialist swarm. */

export type AgentKey = "video" | "document" | "web" | "news";
export type AgentStatus = "idle" | "running" | "done" | "error";

export interface ToolCallTrace {
  toolCallId: string;
  name: string;
  startedAt: number;
  finishedAt: number | null;
}

export type ActivityKind = "step" | "tool" | "done" | "error" | "info";

export interface ActivityEntry {
  ts: number;
  text: string;
  kind: ActivityKind;
}

export interface AgentSlot {
  status: AgentStatus;
  startedAt: number | null;
  finishedAt: number | null;
  toolCalls: ToolCallTrace[];
  activityLog: ActivityEntry[];
  currentStep: string;
  streamText: string;
  toolArgBuffer: Record<string, string>;
}

/** Threat level - paired with score band + color semantics in the UI. */
export type ThreatLevel = "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";

/** One driver/dimension of the overall threat score (e.g. "Product velocity"). */
export interface ThreatDimension {
  key: string;
  label: string;
  score: number;
  max: number;
  note: string;
}

/**
 * Synthesised threat assessment for the analysed competitor.
 * Mirrors the backend THREAT_SCORE event payload exactly (camelCase).
 */
export interface ThreatScore {
  competitor: string;
  score: number;            // 1-10 integer
  level: ThreatLevel;
  rationale: string;
  whyItMattersToUs: string;
  confidence: number;       // 0-100 integer
  dimensions: ThreatDimension[];
  topThreats: string[];
  counterMoves: string[];
  sources: string[];        // URLs
}

/** Collapsible competitive battlecard rendered from markdown. */
export interface Battlecard {
  markdown: string;
  confidence: number;       // 0-100 integer
}

export interface SwarmState {
  runStatus: "idle" | "running" | "done" | "error" | "awaiting-approval";
  agents: Record<AgentKey, AgentSlot>;
  synthesisText: string;
  errorMessage: string | null;
  startedAt: number | null;
  elapsedMs: number | null;
  // toolCallId → AgentKey - correlates TOOL_CALL_END with the right agent slot
  toolCallToAgent: Record<string, AgentKey>;
  // Synthesis layer - fused threat assessment + battle-ready brief.
  threat: ThreatScore | null;
  battlecard: Battlecard | null;
}

const _emptySlot = (): AgentSlot => ({
  status: "idle", startedAt: null, finishedAt: null,
  toolCalls: [], activityLog: [], currentStep: "", streamText: "", toolArgBuffer: {},
});

export const INITIAL_SWARM_STATE: SwarmState = {
  runStatus: "idle",
  agents: {
    video:    _emptySlot(),
    document: _emptySlot(),
    web:      _emptySlot(),
    news:     _emptySlot(),
  },
  synthesisText: "",
  errorMessage: null,
  startedAt: null,
  elapsedMs: null,
  toolCallToAgent: {},
  threat: null,
  battlecard: null,
};

/**
 * Per-agent metadata. `model` is the tasteful Amazon Nova branding shown on each
 * card (a strength to highlight for the AWS Builder Center audience) - the
 * product-facing model NAME only, never a raw model ID / inference profile.
 *
 * Mapping verified against novasynth/config.py + agents/*:
 *   - video    -> us.amazon.nova-lite-v1   (multimodal video understanding)
 *   - document -> us.amazon.nova-2-lite-v1 (text + reasoning over filings)
 *   - web      -> us.amazon.nova-2-lite-v1 + nova_grounding (real-time web)
 *   - news     -> us.amazon.nova-2-lite-v1 + nova_grounding (real-time web search)
 */
export const AGENT_META: Record<AgentKey, { label: string; subtitle: string; model: string }> = {
  video:    { label: "Video Intel",    subtitle: "Launches, demos & keynote coverage", model: "Amazon Nova Lite" },
  document: { label: "Document Intel", subtitle: "Filings, financials & earnings",     model: "Amazon Nova 2 Lite" },
  web:      { label: "Web Signals",    subtitle: "Pricing, reviews & hiring signals",  model: "Amazon Nova 2 Lite + grounding" },
  news:     { label: "News Monitor",   subtitle: "Breaking news & announcements",      model: "Amazon Nova 2 Lite + grounding" },
};

// Strands @tool function names → AgentKey.
// Source: novasynth/agents/orchestrator.py _ALL_TOOLS list.
export const TOOL_TO_AGENT: Record<string, AgentKey> = {
  analyze_video_intel:           "video",
  prepare_and_analyze_video:     "video",
  auto_analyze_competitor_video: "video",
  analyze_document_intel:        "document",
  discover_competitor_documents: "document",
  analyze_competitor_financials: "document",
  analyze_competitor_earnings:   "document",
  analyze_competitor_consensus:  "document",
  analyze_web_signals:           "web",
  scrape_competitor_url:         "web",
  monitor_competitor_news:       "news",
  // search_historical_intel and store_intel_vector are orchestrator-internal
  // and not shown in the per-agent grid.
};

export type SwarmAction =
  | { type: "RUN_STARTED" }
  | { type: "RUN_FINISHED"; elapsedMs: number }
  | { type: "RUN_ERROR"; message: string }
  | { type: "TOOL_CALL_STARTED"; toolCallId: string; toolCallName: string; agentKey?: AgentKey }
  | { type: "TOOL_CALL_ENDED"; toolCallId: string }
  | { type: "TEXT_CHUNK"; delta: string }
  | { type: "AGENT_STEP"; agentKey: AgentKey; text: string; kind: ActivityKind }
  | { type: "TOOL_CALL_ARGS_CHUNK"; toolCallId: string; delta: string }
  | { type: "AGENT_TEXT_CHUNK"; agentKey: AgentKey; delta: string; kind: "text" | "reasoning" }
  | { type: "AGENT_DONE"; agentKey: AgentKey }
  | { type: "THREAT_SCORE_SET"; threat: ThreatScore }
  | { type: "BATTLECARD_SET"; battlecard: Battlecard }
  | { type: "SET_AWAITING_APPROVAL" }
  | { type: "APPROVE" }
  | { type: "RESET" };
